import React from 'react';
import { Link } from 'react-router-dom';

export const PrivacyPage: React.FC = () => {
  return (
    <div>
      <section
        className="text-white text-center py-16 px-4"
        style={{ background: 'linear-gradient(135deg, #2c5530 0%, #4a7c59 100%)' }}
      >
        <h1 className="text-4xl font-semibold mb-3">Privacy Policy</h1>
        <p className="text-lg opacity-90">Collect only what's needed, share even less, and give you control over the rest</p>
      </section>

      <section className="bg-white py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8 text-sm text-yellow-800">
            <strong>Draft — not yet in effect.</strong> This is a working draft pending attorney review. It reflects what the Platform actually collects and shares today. Placeholders (effective date, contact address, entity name) will be filled in before this Policy takes effect.
          </div>

          <p className="text-gray-500 text-sm mb-6"><strong>Effective Date:</strong> [EFFECTIVE DATE]</p>

          <p className="text-gray-700 mb-6">
            This Privacy Policy explains what information <strong>[Pop-Up Yoga LLC]</strong> ("Pop-Up Yoga," "we," "us") collects
            through the Pop-Up Yoga website and applications (the "Platform"), how we use it, who we share it with,
            and the choices you have. We hold ourselves to a simple rule:{' '}
            <strong>collect only what the Platform needs to function, share even less, and give you control over the rest.</strong>
          </p>
          <p className="text-gray-700 mb-8">This Policy applies to all Platform users — Yogis, Leaders, Organizers, and Admins.</p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">1. Information We Collect</h2>
          <h3 className="text-lg font-semibold text-green-800 mb-3">1.1 Information you give us directly</h3>
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-green-50">
                  <th className="border border-gray-200 px-3 py-2 text-left text-green-800">Category</th>
                  <th className="border border-gray-200 px-3 py-2 text-left text-green-800">Examples</th>
                  <th className="border border-gray-200 px-3 py-2 text-left text-green-800">Why we ask for it</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                <tr>
                  <td className="border border-gray-200 px-3 py-2">Account information</td>
                  <td className="border border-gray-200 px-3 py-2">Email address, phone number, display name</td>
                  <td className="border border-gray-200 px-3 py-2">Identity verification and reducing fake/duplicate accounts; essential account communication</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-200 px-3 py-2">Profile information</td>
                  <td className="border border-gray-200 px-3 py-2">Username, default practice location(s), notification preference</td>
                  <td className="border border-gray-200 px-3 py-2">Personalizing the Platform; routing communication per your preference</td>
                </tr>
                <tr>
                  <td className="border border-gray-200 px-3 py-2">Leader profile information</td>
                  <td className="border border-gray-200 px-3 py-2">Bio, years of experience, specialties, certifications</td>
                  <td className="border border-gray-200 px-3 py-2">Helping Yogis discover and evaluate a Leader — entirely Leader-controlled</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-200 px-3 py-2">Leader verification information</td>
                  <td className="border border-gray-200 px-3 py-2">Yoga Alliance profile URL and credential ID</td>
                  <td className="border border-gray-200 px-3 py-2">Used solely by administrators to verify teaching credentials. Never returned in any public-facing API response.</td>
                </tr>
                <tr>
                  <td className="border border-gray-200 px-3 py-2">Event and registration data</td>
                  <td className="border border-gray-200 px-3 py-2">Events you create, register for, lead, or organize; waitlist status</td>
                  <td className="border border-gray-200 px-3 py-2">Operating the core function of the Platform</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-200 px-3 py-2">Role request information</td>
                  <td className="border border-gray-200 px-3 py-2">The reason you give when requesting a Leader or Organizer role</td>
                  <td className="border border-gray-200 px-3 py-2">Reviewed by administrators to evaluate your request</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="text-lg font-semibold text-green-800 mb-3">1.2 Information collected automatically</h3>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
            <li><strong>Authentication session data.</strong> Your session is maintained using your browser's local storage (not third-party tracking cookies).</li>
            <li><strong>Basic server logs.</strong> Standard AWS request logs (timestamps, IP address, error data) used for security and debugging — not for profiling or advertising.</li>
          </ul>

          <h3 className="text-lg font-semibold text-green-800 mb-3">1.3 What we do not collect</h3>
          <p className="text-gray-700 mb-8">
            We do not use third-party advertising trackers, analytics pixels, or cross-site tracking cookies. We do not collect
            your precise location automatically. We do not collect payment information, because the Platform does not yet process payments.
          </p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">2. How We Use Information</h2>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
            <li>Operate account creation, sign-in, and identity verification</li>
            <li>Reduce fake or duplicate accounts</li>
            <li>Enable event discovery, registration, waitlisting, and the leader-assignment workflow</li>
            <li>Send essential communications: registration confirmations, event changes, reminders, and security notices</li>
            <li>Send optional communications you've opted into, and respect your choice to opt out</li>
            <li>Run the Leader role-request and credential verification workflow</li>
            <li>Allow administrators to investigate abuse and keep the Platform safe</li>
            <li>Comply with legal obligations</li>
          </ul>
          <p className="text-gray-700 mb-8">
            We do <strong>not</strong> use your information to build an advertising profile, and we do not use automated
            decision-making that produces legal or similarly significant effects on you.
          </p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">3. How We Share Information — and How We Don't</h2>

          <h3 className="text-lg font-semibold text-green-800 mb-3">3.1 What other users can see</h3>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
            <li><strong>Yogis, Leaders, and Organizers never see each other's email address or phone number.</strong> This is a deliberate design choice — these fields are not included in the API responses other users' apps receive.</li>
            <li>A Leader's public profile shows only what that Leader chooses to disclose. It does not show their email, phone number, or Yoga Alliance verification details.</li>
            <li>Event attendee lists visible to a Leader or Organizer show name only — not contact information.</li>
          </ul>

          <h3 className="text-lg font-semibold text-green-800 mb-3">3.2 What administrators can see</h3>
          <p className="text-gray-700 mb-6">
            Administrators have access to account information (including email and phone number) <strong>only for specified
            administrative purposes</strong>: account recovery, resolving orphaned accounts, investigating reported abuse, and
            managing roles and verification status.
          </p>

          <h3 className="text-lg font-semibold text-green-800 mb-3">3.3 Service providers</h3>
          <p className="text-gray-700 mb-6">
            We use <strong>Amazon Web Services (AWS)</strong> — authentication (Cognito), data storage (DynamoDB), application
            logic (Lambda), file/static hosting (S3, CloudFront), and transactional email (SES). We do not currently use any
            other third-party data processor.
          </p>

          <h3 className="text-lg font-semibold text-green-800 mb-3">3.4 What we do not do</h3>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
            <li><strong>We do not sell your personal information.</strong></li>
            <li><strong>We do not share your personal information for cross-context behavioral advertising.</strong></li>
            <li>We do not share your information with other users beyond what's described in Section 3.1, regardless of role.</li>
          </ul>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">4. Event Communication Without Exposing Contact Information</h2>
          <p className="text-gray-700 mb-4">
            A Leader needs to be able to tell their registered students about a cancellation or change — without your personal
            email or phone number changing hands:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
            <li>Platform-triggered emails are sent by Pop-Up Yoga's own systems, without disclosing your email address to the Leader or other attendees.</li>
            <li>We plan to build dedicated in-platform messaging and notification tools — including future "follow a Leader" functionality — with limits to prevent spam.</li>
          </ul>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">5. SMS Communication</h2>
          <p className="text-gray-700 mb-4">
            If you provide a phone number and opt in, we send transactional text messages under the program{' '}
            <strong>"Pop-Up Yoga Account Alerts."</strong> These messages are limited to: account verification codes,
            class registration confirmations, class cancellation notices, and waitlist openings. We do not send marketing
            or promotional messages by SMS, and we do not use SMS opt-in consent for any purpose beyond this program.
          </p>
          <p className="text-gray-700 mb-4">
            <strong>Consent.</strong> You opt in by selecting SMS as a notification preference during account creation
            or in your profile settings and affirmatively agreeing to receive text messages. Message frequency varies
            with your account activity. Message and data rates may apply. Reply <strong>STOP</strong> to a message at
            any time to opt out, or <strong>HELP</strong> for help; you can also change your notification preference
            from your profile at any time.
          </p>
          <p className="text-gray-700 mb-8">
            <strong>No third-party sharing.</strong> No mobile information collected as part of the SMS consent process
            — including your phone number and opt-in status — will be shared with third parties or affiliates for
            marketing or promotional purposes. This information is excluded from the sharing described in Section 3 and
            is used solely to operate the Pop-Up Yoga Account Alerts program described above.
          </p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">6. Cookies and Tracking</h2>
          <p className="text-gray-700 mb-8">
            We do not use advertising cookies or third-party tracking/analytics scripts. Your signed-in session is maintained
            using your browser's local storage, which is not used to track you across other websites.
          </p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">7. Your Choices and Rights</h2>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
            <li><strong>Update or correct your information</strong> at any time from your Profile page.</li>
            <li><strong>Opt out of non-essential communications</strong> from your account settings. Essential communications tied to account security or an Event you're registered for cannot be opted out of while you maintain that account.</li>
            <li><strong>Delete your account</strong> at any time; see Section 8 for what happens to your data afterward.</li>
          </ul>
          <p className="text-gray-700 mb-8">
            Depending on where you live, you may have additional rights under laws such as the California Consumer Privacy
            Act (CCPA/CPRA), the Virginia Consumer Data Protection Act, or similar laws — including the right to know,
            access, correct, and delete your information, and to opt out of the sale or sharing of personal information for
            advertising. <strong>We do not sell or share your information for advertising</strong>, so there is nothing to
            opt out of today. To exercise any rights, contact us at [PRIVACY CONTACT EMAIL].
          </p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">8. Data Retention</h2>
          <p className="text-gray-700 mb-8">
            We retain your information for as long as your account is active or as needed to operate the Platform. After you
            delete your account, most personal information is deleted from active systems within a reasonable period. We may
            retain limited information where necessary for legal compliance, safety/abuse investigation, or dispute resolution.
          </p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">9. Data Security</h2>
          <p className="text-gray-700 mb-8">
            We use reasonable administrative, technical, and physical safeguards to protect your information, including
            running on AWS infrastructure with encryption in transit. No system is perfectly secure.
          </p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">10. Children's Privacy</h2>
          <p className="text-gray-700 mb-8">
            The Platform is not directed to anyone under 18 (see Section 2 of the <Link to="/terms" className="text-green-700 hover:underline">Terms of Service</Link>).
            If you believe a minor has provided us with personal information, contact us at [PRIVACY CONTACT EMAIL].
          </p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">11. International Users</h2>
          <p className="text-gray-700 mb-8">
            The Platform is intended for users in the United States. If you access it from outside the US, your information
            will be transferred to and processed in the United States.
          </p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">12. Changes to This Policy</h2>
          <p className="text-gray-700 mb-8">
            We may update this Policy from time to time. If we make material changes, we will provide notice (such as an
            in-app notice or email) before the changes take effect.
          </p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">13. Contact Us</h2>
          <p className="text-gray-700 mb-8">
            Questions about this Policy or your privacy rights can be sent to [PRIVACY CONTACT EMAIL].
          </p>
        </div>
      </section>

      <footer className="bg-gray-800 text-white text-center py-8">
        <p className="text-gray-400 text-sm">&copy; {new Date().getFullYear()} Pop-Up Yoga. All rights reserved.</p>
        <p className="text-gray-500 text-xs mt-2">
          <Link to="/privacy" className="hover:text-gray-300 transition-colors">Privacy Policy</Link>
          {' · '}
          <Link to="/terms" className="hover:text-gray-300 transition-colors">Terms of Service</Link>
        </p>
      </footer>
    </div>
  );
};
